import React from "react";

/*
 * Single source of truth for inline icons.
 *
 * Usage:
 *   <Icon name="trash" size={16} />
 *   <Icon name="check" size={18} className="my-icon" />
 *
 * All icons follow the Lucide convention: 24x24 viewBox, strokes only,
 * currentColor, rounded caps/joins. Add a new icon by appending an entry
 * to ICONS - the entry is the inner SVG children for that icon.
 */

const ICONS = {
    trash: (
        <>
            <path d="M3 6H21" />
            <path d="M8 6V4A2 2 0 0 1 10 2H14A2 2 0 0 1 16 4V6" />
            <path d="M5 6V20A2 2 0 0 0 7 22H17A2 2 0 0 0 19 20V6" />
            <path d="M10 11V17" />
            <path d="M14 11V17" />
        </>
    ),
    edit: (
        <>
            <path d="M12 20H21" />
            <path d="M16.5 3.5A2.121 2.121 0 0 1 19.5 6.5L7 19L3 20L4 16L16.5 3.5Z" />
        </>
    ),
    x: <path d="M18 6L6 18M6 6L18 18" />,
    "chevron-down": <path d="M6 9L12 15L18 9" />,
    "chevron-up": <path d="M18 15L12 9L6 15" />,
    check: <path d="M5 12L10 17L20 7" />,
    "arrow-right": <path d="M5 12H19M19 12L12 5M19 12L12 19" />,
    search: (
        <>
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21L16.65 16.65" />
        </>
    ),
    mail: (
        <>
            <path d="M4 4H20A2 2 0 0 1 22 6V18A2 2 0 0 1 20 20H4A2 2 0 0 1 2 18V6A2 2 0 0 1 4 4Z" />
            <path d="M22 6L12 13L2 6" />
        </>
    ),
    user: (
        <>
            <path d="M20 21V19A4 4 0 0 0 16 15H8A4 4 0 0 0 4 19V21" />
            <circle cx="12" cy="7" r="4" />
        </>
    ),
    lock: (
        <>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7A5 5 0 0 1 17 7V11" />
        </>
    ),
    "alert-circle": (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8V13" />
            <path d="M12 17H12.01" />
        </>
    ),
    calendar: (
        <>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2V6" />
            <path d="M8 2V6" />
            <path d="M3 10H21" />
        </>
    ),
    "badge-check": (
        <>
            <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
            <path d="m9 12 2 2 4-4" />
        </>
    ),
    "more-horizontal": (
        <>
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
        </>
    ),
    home: (
        <>
            <path d="M3 9L12 2L21 9V20A2 2 0 0 1 19 22H5A2 2 0 0 1 3 20Z" />
            <path d="M9 22V12H15V22" />
        </>
    ),
    // WhatsApp-shaped chat bubble. Kept brand-recognisable without using the
    // official WhatsApp wordmark (which would require licensing).
    whatsapp: (
        <>
            <path d="M21 12a9 9 0 1 1-3.45-7.08l.45.35L21 4l-1.27 3.27.35.45A9 9 0 0 1 21 12Z" />
            <path d="M8.5 9.5C8.5 9 9 8.5 9.5 8.5H10C10.5 8.5 10.7 9 10.9 9.5L11.3 10.4C11.4 10.7 11.4 11 11.2 11.2L10.7 11.7C11.4 13 12.2 13.6 13.3 14.3L13.8 13.8C14 13.6 14.3 13.6 14.6 13.7L15.5 14.1C16 14.3 16.5 14.5 16.5 15V15.5C16.5 16 16 16.5 15.5 16.5C12.5 16.5 8.5 12.5 8.5 9.5Z" />
        </>
    ),
};

const Icon = ({ name, size = 16, strokeWidth = 2, className, style, ...rest }) => {

    const children = ICONS[name];

    if (!children) {
        if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn(`<Icon> unknown name: "${name}"`);
        }
        return null;
    }

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            style={style}
            aria-hidden="true"
            {...rest}
        >
            {children}
        </svg>
    );

};

export default Icon;
